import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import { LenderTag } from "../../types/deals";
import { StatusTimeline } from "./StatusTimeline";
import { getStatusConfig } from "../../utils/statusConfig";
import { 
  Building2, User, IndianRupee, FileText, Upload, 
  Edit2, Check, X, AlertCircle, TrendingUp, Clock, Download, Shield
} from "lucide-react";
import { format, parseISO } from "date-fns";

interface EnhancedViewTaggedLenderDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lender: LenderTag | null;
  onFileUpload?: (type: "sanctionLetter", file: File) => void;
  onNoteUpdate?: (lenderId: string, note: string) => void;
  LenderStatus: any[];
}

export function EnhancedViewTaggedLenderDialog({ 
  open, 
  onOpenChange, 
  lender, 
  onFileUpload, 
  onNoteUpdate,
  LenderStatus,
}: EnhancedViewTaggedLenderDialogProps) {
  const [editingNote, setEditingNote] = useState(false);
  const [noteValue, setNoteValue] = useState("");

  if (!lender) return null;

  // Extract key information
  const lastSanctionEntry = [...lender.statusHistory]
    .reverse()
    .find(e => typeof e.sanctionAmount === 'number');
  
  const lastDisbursed = [...lender.statusHistory]
    .reverse()
    .find(e => typeof e.disbursedAmount === 'number');
  
  const latestStatus = lender.statusHistory[lender.statusHistory.length - 1];
  const statusConfig = getStatusConfig(LenderStatus.find((s: any) => s.id === latestStatus?.statusId)?.name as any);
  const StatusIcon = statusConfig?.icon;

  // Document details - only sanction letter
  const sanctionLetterDoc = [...lender.statusHistory]
    .reverse()
    .find(e => e.sanctionLetterName);

  // Financial calculations
  const totalRevenue = lastDisbursed?.totalRevenue || 0;
  const cycleTime = latestStatus ? 
    Math.ceil((new Date(latestStatus.timestamp).getTime() - new Date(lender.taggedAt).getTime()) / (1000 * 60 * 60 * 24)) : 0;

  const handleFileUpload = (type: "sanctionLetter") => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.xlsx,.xls';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && onFileUpload) {
        onFileUpload(type, file);
      }
    };
    input.click();
  };

  const handleEditNote = () => {
    setNoteValue(lender.note || "");
    setEditingNote(true);
  };

  const handleSaveNote = () => {
    if (onNoteUpdate) {
      onNoteUpdate(lender.id as string, noteValue);
    }
    setEditingNote(false);
  };

  const handleCancelNote = () => {
    setEditingNote(false);
    setNoteValue("");
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    try {
      return format(parseISO(dateStr), "dd MMM yyyy");
    } catch {
      return dateStr;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">{lender.institution}</span>
              </div>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{lender.lenderName}</span>
              </div>
            </SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="px-6 py-4 space-y-6">
              {/* Current Status Banner */}
              <Card className="border-l-4 border-l-primary">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {StatusIcon && (
                        <div className={`p-2 rounded-full bg-muted ${statusConfig?.color}`}>
                          <StatusIcon className="h-4 w-4" />
                        </div>
                      )}
                      <div>
                        <Badge variant={statusConfig?.variant || "outline"} className="mb-1">
                          {LenderStatus.find((s: any) => s.id === latestStatus?.statusId)?.name as any   || "No Status"}
                        </Badge>
                        <p className="text-sm text-muted-foreground">
                          {LenderStatus.find((s: any) => s.id === latestStatus?.statusId)?.description as any || "Current status"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {cycleTime} days
                      </div>
                      <p className="text-xs text-muted-foreground">Cycle time</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Basic Information */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Lender Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Tagged Date</span>
                      <p className="font-medium">{new Date(lender.taggedAt).toLocaleDateString("en-GB")}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Application RM</span>
                      <p className="font-medium">{lender.applicationRM || "Not specified"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Program Limit</span>
                      <p className="font-medium flex items-center gap-1">
                        <IndianRupee className="h-3 w-3" />
                        {lender.programLimit ? 
                          new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(lender.programLimit) : 
                          "Not specified"
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Lender ID</span>
                      <p className="font-medium font-mono text-xs">{lender.id}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sanction Details (if available) */}
              {lastSanctionEntry && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Sanction Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Sanction Amount</span>
                        <p className="font-medium flex items-center gap-1 text-lg">
                          <IndianRupee className="h-4 w-4" />
                          {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(lastSanctionEntry.sanctionAmount!)}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sanction Date</span>
                        <p className="font-medium">{formatDate(lastSanctionEntry.sanctionDate)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Valid Till</span>
                        <p className="font-medium">{formatDate(lastSanctionEntry.sanctionValidTill)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Status Updated</span>
                        <p className="font-medium">{formatDate(lastSanctionEntry.timestamp)}</p>
                      </div>
                    </div>
                    
                    {lastSanctionEntry.collateralDetails && (
                      <div className="mt-4 p-3 rounded-lg bg-muted/50 border">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Collateral/Security Details</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{lastSanctionEntry.collateralDetails}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Editable Note */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    Notes
                    {!editingNote && (
                      <Button variant="ghost" size="sm" onClick={handleEditNote}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editingNote ? (
                    <div className="space-y-3">
                      <Textarea
                        value={noteValue}
                        onChange={(e) => setNoteValue(e.target.value)}
                        placeholder="Add notes about this lender..."
                        rows={3}
                        className="resize-none"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveNote}>
                          <Check className="h-4 w-4 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancelNote}>
                          <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {lender.note || "No notes added yet. Click edit to add notes."}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Financial Details */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Financial Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-3">
                      <div>
                        <span className="text-muted-foreground">Sanctioned Amount</span>
                        <p className="font-medium flex items-center gap-1">
                          <IndianRupee className="h-3 w-3" />
                          {lastSanctionEntry?.sanctionAmount ? 
                            new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(lastSanctionEntry.sanctionAmount) : 
                            "Not sanctioned"
                          }
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Disbursed Amount</span>
                        <p className="font-medium flex items-center gap-1">
                          <IndianRupee className="h-3 w-3" />
                          {lastDisbursed?.disbursedAmount ? 
                            new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(lastDisbursed.disbursedAmount) : 
                            "Not disbursed"
                          }
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <span className="text-muted-foreground">Total Revenue</span>
                        <p className="font-medium flex items-center gap-1">
                          <IndianRupee className="h-3 w-3" />
                          {totalRevenue ? 
                            new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(totalRevenue) : 
                            "Not calculated"
                          }
                        </p>
                      </div>
                      {lastDisbursed && (
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div>Customer: ₹{new Intl.NumberFormat("en-IN").format(lastDisbursed.customerPayout || 0)}</div>
                          <div>Lender: ₹{new Intl.NumberFormat("en-IN").format(lastDisbursed.lenderPayout || 0)}</div>
                          <div>Partner: ₹{new Intl.NumberFormat("en-IN").format(lastDisbursed.partnerPayout || 0)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Document Management - Only Sanction Letter */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">Sanction Letter</h4>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleFileUpload("sanctionLetter")}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        {sanctionLetterDoc ? "Re-upload" : "Upload"}
                      </Button>
                    </div>
                    {sanctionLetterDoc ? (
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span>{sanctionLetterDoc.sanctionLetterName}</span>
                        </div>
                        {sanctionLetterDoc.sanctionLetterUrl && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={sanctionLetterDoc.sanctionLetterUrl} target="_blank" rel="noopener noreferrer">
                              <Download className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <AlertCircle className="h-4 w-4" />
                        No sanction letter uploaded
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Status Timeline */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Status Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <StatusTimeline entries={lender.statusHistory} />
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}
